import { Injectable } from '@angular/core';
import { catchError, combineLatest, map, Observable, of, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { DeviceAuthenticationStatusService } from '@shure/shared/angular/data-access/device-authentication';
import {
	ApolloQueryErrorMapper,
	ApolloQueryError,
	CombiningSubscriptionManager,
	NodeChangeType,
	SubscriptionManagerService,
	waitForGQLCompleteQueryOrThrow
} from '@shure/shared/angular/data-access/system-api/core';
import {
	DeviceNtpNetworkProtocolMode,
	Response,
	ResponseState
} from '@shure/shared/angular/data-access/system-api/models';
import { DeviceReadonlyStateProvider } from '@shure/shared/angular/utils/device-readonly';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { noMoreSubscribers, tapOnce } from '@shure/shared/typescript/rxjs';

import { NtpDateTimeSettingsApiService, NtpDateTimeSettingsDevice } from '../api';

import {
	NtpDateTimeSettingsDeviceQueryGQL,
	NtpDateTimeSettingsDeviceSubscriptionGQL,
	UpdateNtpServerConfigurationMutationGQL,
	ToggleNetworkTimeProtocolMutationGQL
} from './graphql/generated/ntp-date-time.generated';
import { mapNtpDateTimeSettingsFromSysApi } from './mappers';

@Injectable()
export class SysApiNtpDateTimeApiService implements NtpDateTimeSettingsApiService {
	private readonly logger: ILogger;

	private readonly ntpDateTimeSettingsDeviceSubscriptionManager: CombiningSubscriptionManager;

	constructor(
		logger: ILogger,
		private readonly ntpDateTimeSettingsDeviceQueryGQL: NtpDateTimeSettingsDeviceQueryGQL,
		private readonly toggleNetworkTimeProtocolMutationGQL: ToggleNetworkTimeProtocolMutationGQL,
		private readonly updateNtpServerConfigurationMutationGQL: UpdateNtpServerConfigurationMutationGQL,
		private readonly ntpDateTimeSettingsDeviceSubscriptionGQL: NtpDateTimeSettingsDeviceSubscriptionGQL,
		private readonly deviceAuthenticationStatusService: DeviceAuthenticationStatusService,
		private readonly readonlyStateProvider: DeviceReadonlyStateProvider,
		private readonly subscriptionManagerService: SubscriptionManagerService
	) {
		this.logger = logger.createScopedLogger('SysApiNtpDateTimeApiService');

		this.ntpDateTimeSettingsDeviceSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'SysApiNtpDateTimeApiService:NtpDateTimeSettingsDeviceSubscriptionManager',
				changeTypes: [
					NodeChangeType.DeviceNetworkTimeProtocolServerConfiguration,
					NodeChangeType.DeviceNetworkTimeProtocol,
					NodeChangeType.DeviceAuthentication
				],
				subscriptionGql: ntpDateTimeSettingsDeviceSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}

	public getDevice$(deviceId: string): Observable<NtpDateTimeSettingsDevice> {
		const queryId = uuidv4();
		this.logger.trace('getDevice$()', 'Querying device NTP date-time settings', {
			deviceId,
			queryId
		});

		return combineLatest([
			this.ntpDateTimeSettingsDeviceQueryGQL
				.watch({ variables: { deviceId }, errorPolicy: 'all' })
				.valueChanges.pipe(waitForGQLCompleteQueryOrThrow()),
			this.deviceAuthenticationStatusService.getDeviceState$(deviceId),
			this.readonlyStateProvider.isReadonly$(deviceId)
		]).pipe(
			map(([query, auth, isReadonly]) => {
				if (query.data?.node && 'features' in query.data.node) {
					const device = mapNtpDateTimeSettingsFromSysApi(
						query.data.node as Parameters<typeof mapNtpDateTimeSettingsFromSysApi>[0],
						auth.authenticated,
						isReadonly
					);

					this.logger.trace('getDevice$()', 'Done', device);
					return device;
				}

				this.logger.error('getDevice$()', 'Failed to query device NTP date-time settings', { deviceId });
				throw ApolloQueryErrorMapper.getError(query);
			}),
			tapOnce((device) => this.ntpDateTimeSettingsDeviceSubscriptionManager.subscribe(queryId, device.id)),
			noMoreSubscribers(() => {
				this.logger.trace('ntpDateTimeSettingsDeviceSubscriptionManager.onSubscribers.noMoreSubscribers', '', {
					deviceId,
					queryId
				});
				this.ntpDateTimeSettingsDeviceSubscriptionManager.unsubscribeQuery(queryId);
			}),
			catchError((error: Error) => {
				this.logger.error('getDevice$()', 'Failed to query device NTP date-time settings', {
					id: deviceId,
					error
				});
				return throwError(() => new ApolloQueryError(error));
			})
		);
	}
	public toggleNetworkTimeProtocol(deviceId: string, enable: boolean): Observable<Response<void, string>> {
		this.logger.trace('toggleNetworkTimeProtocol()', 'toggle NTP protocol', { deviceId });

		return this.toggleNetworkTimeProtocolMutationGQL
			.mutate({
				variables: {
					updates: [
						{
							device: {
								features: {
									networkTimeProtocol: {
										enabled: enable
									}
								},
								id: deviceId
							}
						}
					],
					injectKeyring: '//supplied-by-keyring-injector'
				},
				errorPolicy: 'all'
			})
			.pipe(
				map((result) => {
					if (
						result.data?.updateNodes &&
						'error' in result.data.updateNodes[0] &&
						result.data.updateNodes[0].error !== null
					) {
						this.logger.error('toggleNetworkTimeProtocol()', 'Failed to toggle NTP protocol', {
							deviceId,
							result
						});
						return { state: ResponseState.Error, error: result.data.updateNodes[0].error?.message };
					}

					this.logger.trace('toggleNetworkTimeProtocol()', 'Done', { deviceId });
					return { state: ResponseState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('toggleNetworkTimeProtocol()', 'Error', { error });
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}
	public updateNtpServerConfiguration(
		deviceId: string,
		mode: DeviceNtpNetworkProtocolMode,
		manualConfiguration: string[]
	): Observable<Response<void, string>> {
		this.logger.trace('updateNtpServerConfiguration()', 'Updating device NTP configurations', { deviceId });

		return this.updateNtpServerConfigurationMutationGQL
			.mutate({
				variables: {
					updates: [
						{
							device: {
								features: {
									networkTimeProtocolServerConfiguration: {
										mode,
										manualConfiguration
									}
								},
								id: deviceId
							}
						}
					],
					injectKeyring: '//supplied-by-keyring-injector'
				},
				errorPolicy: 'all'
			})
			.pipe(
				map((result) => {
					if (
						result.data?.updateNodes &&
						'error' in result.data.updateNodes[0] &&
						result.data.updateNodes[0].error !== null
					) {
						this.logger.error(
							'updateNtpServerConfiguration()',
							'Failed to update device NTP configurations',
							{
								deviceId,
								result
							}
						);
						return { state: ResponseState.Error, error: result.data.updateNodes[0].error?.message };
					}

					this.logger.trace('updateNtpServerConfiguration()', 'Done', { deviceId });
					return { state: ResponseState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('updateNtpServerConfiguration()', 'Error', { error });
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}
}
